import React, {useMemo} from 'react';
import moment from 'moment';
import {useSelector} from 'react-redux';
import {t} from '../../../../../../services/i18n';
import {dbDateFormat, formatPriceWithCurrencyLTR, sortCourseEventDays, daysEnum} from '../../../../../../services/Helpers';
import {GlobalStyleAttributes} from '../../../../../../styles/GlobalStyles';
import HStack from '../../../../../common/Layout/HStack';
import AppText from '../../../../../common/AppText';
const COL_WIDTHS = {
    name: '230px',
    price: '150px',
    date: '190px',
    days: '170px',
};

const COL_WIDTHS_WITH_LOCATION = {
    name: '230px',
    price: '150px',
    date: '190px',
    days: '110px',
    location: '60px',
};

const ellipsisStyle = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

const HugCollapseLabel = ({ hug, showLocation }) => {
    const box = useSelector((state) => state.box.box);
    const dateFormat = useMemo(() => (box ? box.locations_box[0].date_format : null), [box]);
    const currencySymbol = useMemo(() => box?.locations_box?.[0]?.currency_symbol ?? '₪', [box]);

    const formatDateRange = () => {
        if (!hug.start_date) return null;
        const start = moment(hug.start_date, dbDateFormat).format(dateFormat ?? 'DD/MM/YY');
        if (!hug.end_date) return t('start-on', { start });
        const end = moment(hug.end_date, dbDateFormat).format(dateFormat ?? 'DD/MM/YY');
        return `${start} - ${end}`;
    };

    const formatBasePrice = () => {
        const recurringMt = hug.membership_types_all?.find(mt => mt.type === 'recurring');
        const regularMt = hug.membership_types_all?.find(mt => mt.type === 'regular');

        const parts = [];
        if (recurringMt) {
            parts.push(t('price-recurring-payment', { price: formatPriceWithCurrencyLTR(recurringMt.price, currencySymbol) }));
        }
        if (regularMt) {
            parts.push(formatPriceWithCurrencyLTR(regularMt.price, currencySymbol));
        }

        if (!parts.length) return null;
        return parts.join(` ${t('or-price')} `);
    };

    const formatDays = () => {
        const isOneDayOfWeek = hug.series_events.length === 1
        let days = sortCourseEventDays(hug.series_events).map(day => {
            if (isOneDayOfWeek) return t(`day-${day.day_of_week + 1}`)
            const dayName = daysEnum[day.day_of_week]
            return t(`${dayName}Shortened`)
        })
        return isOneDayOfWeek ? t('in-day', {day: days[0]}) : t('in-days', {days: days.join(', ')})
    }

    const widths = showLocation ? COL_WIDTHS_WITH_LOCATION : COL_WIDTHS;

    return (
        <HStack alignItems={'center'} flex={1} style={{ minWidth: 0 }}>
            <AppText
                fontSize={GlobalStyleAttributes.fonts.heading3}
                boldFont
                style={{ width: widths.name, flexShrink: 0, ...ellipsisStyle }}
            >
                {hug.series_name}
            </AppText>
            <HStack alignItems={'center'} space={'0.25rem'} style={{ width: widths.price, flexShrink: 0 }}>
                <AppText fontSize={GlobalStyleAttributes.fonts.heading3} style={ellipsisStyle}>
                    {formatBasePrice() ?? '—'}
                </AppText>
            </HStack>
            <AppText fontSize={GlobalStyleAttributes.fonts.heading3} style={{ width: widths.date, flexShrink: 0, ...ellipsisStyle }}>
                {formatDateRange() ?? '—'}
            </AppText>
            <AppText fontSize={GlobalStyleAttributes.fonts.heading3} style={{ width: widths.days, flexShrink: 0, ...ellipsisStyle }}>
                {formatDays() ?? '—'}
            </AppText>
            {showLocation && (
                <AppText fontSize={GlobalStyleAttributes.fonts.heading3} style={{ width: widths.location, flexShrink: 0, ...ellipsisStyle }}>
                    {hug.locations_box?.location ?? '—'}
                </AppText>
            )}
        </HStack>
    );
};

export default HugCollapseLabel;
